import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Modal,
  TextField,
  BlockStack,
  InlineStack,
  Button,
  Text,
  Banner,
  Spinner,
  Thumbnail,
  Box,
  Badge,
  Collapsible,
} from '@shopify/polaris';
import { useApi } from '../providers/ApiProvider';
import { ensureCatalogAccess } from '../lib/ensureCatalogAccess';
import { t } from '../i18n';

export interface CatalogVariant {
  id: string;
  title: string;
  price?: string | null;
  available?: boolean;
}

export interface CatalogItem {
  id: string;
  title: string;
  kind: 'product' | 'collection' | 'variant';
  handle?: string;
  imageUrl?: string | null;
  tags?: string[];
  productId?: string;
  collectionId?: string;
  quantity?: number;
  productCount?: number | null;
  /** Default / first variant */
  variantId?: string | null;
  price?: string | null;
  /** Product title when this row is a variant selection */
  productTitle?: string;
  variants?: CatalogVariant[];
  /** Total variants on the parent product (for “2 of 8 variants” labels) */
  totalVariants?: number;
  /**
   * Product / variant available for sale.
   * false = out of stock (still selectable so merchants can set up gifts + see OOS warning).
   */
  available?: boolean;
}

interface CatalogResponse {
  source: 'shopify' | 'error' | 'demo_fallback';
  shop?: string;
  items: CatalogItem[];
  message?: string;
  pageInfo?: { hasNextPage?: boolean; endCursor?: string | null };
  totalCount?: number | null;
}

interface CatalogPickerModalProps {
  open: boolean;
  mode: 'products' | 'collections';
  title?: string;
  /**
   * Selected ids for checkbox state:
   * - product-level pick → product GID / numeric product id
   * - variant pick → that variant's GID / numeric id only
   * Do NOT put productId into this list for partial variant selections —
   * it makes every variant look selected.
   */
  selectedIds: string[];
  tags?: string[];
  onClose: () => void;
  /** Toggle select/deselect one item (product, collection, or variant row) */
  onToggle: (item: CatalogItem) => void;
  /**
   * Optional atomic multi-toggle (e.g. select/deselect all variants).
   * Prefer this over N× onToggle so React state does not thrash.
   */
  onToggleMany?: (items: CatalogItem[], action: 'add' | 'remove') => void;
  onConfirm?: (items: CatalogItem[]) => void;
}

export function idMatch(a: string, b: string) {
  if (!a || !b) return false;
  if (a === b) return true;
  const strip = (s: string) =>
    s
      .replace(/^gid:\/\/shopify\/(Product|ProductVariant|Collection)\//, '')
      .replace(/\D/g, '');
  const na = strip(a);
  const nb = strip(b);
  return !!(na && nb && na === nb);
}

function variantLabel(v: CatalogVariant): string {
  const t = String(v.title || '').trim();
  if (!t || t === 'Default Title' || t.toLowerCase() === 'default title') {
    return 'Default';
  }
  return t;
}

/**
 * Product/collection picker with variant expansion (Shopify-style).
 */
export function CatalogPickerModal({
  open,
  mode,
  title,
  selectedIds,
  tags,
  onClose,
  onToggle,
  onToggleMany,
}: CatalogPickerModalProps) {
  const api = useApi();
  const [query, setQuery] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');
  const [items, setItems] = useState<CatalogItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sourceNote, setSourceNote] = useState<string | null>(null);
  const [endCursor, setEndCursor] = useState<string | null>(null);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  const tagFilter = (tags || []).map((t) => t.trim()).filter(Boolean);
  const tagsKey = tagFilter.join(',');
  const tagsKeyRef = useRef(tagsKey);
  tagsKeyRef.current = tagsKey;

  useEffect(() => {
    if (!open) return;
    const t = window.setTimeout(() => setDebouncedQ(query.trim()), 300);
    return () => window.clearTimeout(t);
  }, [query, open]);

  useEffect(() => {
    if (open) {
      setQuery('');
      setDebouncedQ('');
      setError(null);
      setEndCursor(null);
      setHasNextPage(false);
      setItems([]);
      setSourceNote(null);
      setExpanded({});
    }
  }, [open]);

  const load = useCallback(
    async (opts?: { after?: string | null; append?: boolean; q?: string }) => {
      if (!open) return;
      const append = !!opts?.append;
      if (append) setLoadingMore(true);
      else setLoading(true);
      setError(null);

      const activeTags = tagsKeyRef.current
        ? tagsKeyRef.current.split(',').filter(Boolean)
        : [];
      const nameQ = opts?.q !== undefined ? opts.q : debouncedQ;

      try {
        const shop =
          new URLSearchParams(window.location.search).get('shop') ||
          (() => {
            try {
              return sessionStorage.getItem('gf_shop') || '';
            } catch {
              return '';
            }
          })();
        if (shop) await ensureCatalogAccess(shop);

        if (mode === 'products') {
          const params = new URLSearchParams();
          if (nameQ) params.set('q', nameQ);
          if (activeTags.length) params.set('tags', activeTags.join(','));
          params.set('first', '50');
          const res = await api.get<CatalogResponse>(
            `/stores/catalog/products?${params.toString()}`,
          );
          let next = (res.items || []).map((i) => ({
            ...i,
            kind: 'product' as const,
          }));
          if (nameQ) {
            const qLow = nameQ.toLowerCase();
            next = next.filter(
              (i) =>
                i.title.toLowerCase().includes(qLow) ||
                (i.handle || '').toLowerCase().includes(qLow) ||
                (i.variants || []).some((v) =>
                  String(v.title || '').toLowerCase().includes(qLow),
                ),
            );
          }
          setItems(next);
          // Auto-expand products that have multiple variants
          const exp: Record<string, boolean> = {};
          next.forEach((p) => {
            if ((p.variants || []).length > 1) exp[p.id] = true;
          });
          setExpanded((prev) => ({ ...exp, ...prev }));
          setHasNextPage(false);
          setEndCursor(null);
          setSourceNote(
            res.source === 'shopify'
              ? null
              : res.message || 'Could not load products from Shopify.',
          );
        } else {
          const params = new URLSearchParams();
          if (nameQ) params.set('q', nameQ);
          params.set('first', '50');
          if (opts?.after) params.set('after', opts.after);
          const res = await api.get<CatalogResponse>(
            `/stores/catalog/collections?${params.toString()}`,
          );
          const next = (res.items || []).map((i) => ({
            ...i,
            kind: 'collection' as const,
          }));
          setItems((prev) => (append ? [...prev, ...next] : next));
          setHasNextPage(!!res.pageInfo?.hasNextPage);
          setEndCursor(res.pageInfo?.endCursor || null);
          setSourceNote(
            res.source === 'shopify'
              ? null
              : res.message || 'Could not load collections from Shopify.',
          );
        }
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Failed to load catalog');
        if (!append) setItems([]);
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [api, open, mode, debouncedQ],
  );

  useEffect(() => {
    if (!open) return;
    void load({ q: debouncedQ });
  }, [open, debouncedQ, tagsKey, load]);

  const isSelected = (id: string) =>
    !!id && selectedIds.some((sid) => idMatch(sid, id));

  /**
   * True only when the *product* id is selected (whole product), not a single
   * default/first variant. Including product.variantId here made selecting
   * the main variant look like "All variants" and broke per-variant toggles.
   */
  function productLevelSelected(product: CatalogItem): boolean {
    if (isSelected(product.id)) return true;
    if (product.productId && isSelected(product.productId)) return true;
    const num = String(product.id || '')
      .replace(/^gid:\/\/shopify\/Product\//, '')
      .replace(/\D/g, '');
    if (num && selectedIds.some((sid) => idMatch(sid, num))) return true;
    // Do NOT treat product.variantId (first variant) as whole-product select
    return false;
  }

  /** Variant row checked: explicit variant id OR whole product selected */
  function variantRowSelected(product: CatalogItem, variantId: string): boolean {
    if (productLevelSelected(product)) return true;
    return isSelected(variantId);
  }

  /** Count selected variants for a product (UI badge) */
  function selectedVariantCount(product: CatalogItem): number {
    const vs = product.variants || [];
    // Whole product selected at product-id level
    if (productLevelSelected(product)) {
      return Math.max(1, vs.length || 1);
    }
    if (!vs.length) {
      return isSelected(product.id) ? 1 : 0;
    }
    return vs.filter((v) => isSelected(v.id)).length;
  }

  function productFullySelected(product: CatalogItem): boolean {
    // Product-level pick (common after wizard normalizes id → productId)
    if (productLevelSelected(product)) return true;
    const vs = product.variants || [];
    if (!vs.length) {
      return isSelected(product.id);
    }
    if (vs.length === 1) {
      // Single-variant product: product id OR that variant counts as full
      return isSelected(vs[0].id) || isSelected(product.id);
    }
    // Multi-variant: all variants selected
    return vs.every((v) => isSelected(v.id));
  }

  function productPartiallySelected(product: CatalogItem): boolean {
    if (productFullySelected(product)) return false;
    const n = selectedVariantCount(product);
    return n > 0;
  }

  /** True when every variant (or the product) is unavailable for sale */
  function productOutOfStock(product: CatalogItem): boolean {
    const vs = product.variants || [];
    if (vs.length > 0) {
      return vs.every((v) => v.available === false);
    }
    return product.available === false;
  }

  function toVariantCatalogItem(
    product: CatalogItem,
    v: CatalogVariant,
  ): CatalogItem {
    const productNumeric =
      product.productId ||
      String(product.id).replace(/^gid:\/\/shopify\/Product\//, '');
    const total =
      product.totalVariants ??
      ((product.variants || []).length || undefined);
    return {
      id: v.id,
      kind: 'variant',
      title: `${product.title} — ${variantLabel(v)}`,
      productTitle: product.title,
      productId: productNumeric,
      variantId: v.id,
      imageUrl: product.imageUrl,
      handle: product.handle,
      price: v.price,
      quantity: 1,
      totalVariants: total,
      available: v.available !== false,
    };
  }

  function productRowPayload(product: CatalogItem, v0?: CatalogVariant): CatalogItem {
    return {
      ...product,
      kind: 'product',
      id: product.id,
      productId:
        product.productId ||
        String(product.id).replace(/^gid:\/\/shopify\/Product\//, ''),
      variantId: v0?.id || product.variantId || null,
      price: v0?.price ?? product.price,
      available:
        (v0 ? v0.available !== false : true) && product.available !== false,
    };
  }

  function toggleProductAll(product: CatalogItem) {
    const vs = product.variants || [];

    // Single / no variant list — toggle as one product row
    if (vs.length <= 1) {
      onToggle(productRowPayload(product, vs[0]));
      return;
    }

    // Multi-variant: operate on variant rows only (never product-id-only),
    // so partial selection and checkboxes stay accurate.
    const variantItems = vs.map((v) => toVariantCatalogItem(product, v));
    const selectedCount = vs.filter((v) => isSelected(v.id)).length;
    const allOn = productLevelSelected(product) || selectedCount === vs.length;

    if (allOn || selectedCount > 0) {
      // Full or partial → clear every selected variant of this product
      if (productLevelSelected(product)) {
        onToggle(productRowPayload(product));
      }
      const toRemove = variantItems.filter((it) => isSelected(it.id));
      if (toRemove.length) {
        if (onToggleMany) onToggleMany(toRemove, 'remove');
        else toRemove.forEach((it) => onToggle(it));
      }
      return;
    }

    // None selected → select every variant
    if (onToggleMany) onToggleMany(variantItems, 'add');
    else variantItems.forEach((it) => onToggle(it));
  }

  function toggleVariant(product: CatalogItem, v: CatalogVariant) {
    // Whole product selected → replace with all other variants (deselect this one)
    if (productLevelSelected(product)) {
      const vs = product.variants || [];
      onToggle(productRowPayload(product));
      const others = vs
        .filter((other) => other.id !== v.id)
        .map((other) => toVariantCatalogItem(product, other));
      if (others.length) {
        if (onToggleMany) onToggleMany(others, 'add');
        else others.forEach((it) => onToggle(it));
      }
      return;
    }
    onToggle(toVariantCatalogItem(product, v));
  }

  const searchPlaceholder =
    mode === 'collections'
      ? 'Search collections by name…'
      : 'Search products by name…';

  const selectedCount = selectedIds.length;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={
        title ||
        (mode === 'products' ? 'Select products' : 'Select collections')
      }
      primaryAction={{ content: t("Done"), onAction: onClose }}
      secondaryActions={[{ content: t("Cancel"), onAction: onClose }]}
    >
      <Modal.Section>
        <BlockStack gap="300">
          <TextField
            label={t("Search by name")}
            labelHidden
            value={query}
            onChange={setQuery}
            autoComplete="off"
            placeholder={searchPlaceholder}
            clearButton
            onClearButtonClick={() => setQuery('')}
            connectedRight={
              <Button
                onClick={() => void load({ q: query.trim() })}
                loading={loading}
              >
                {t("Refresh")}
              </Button>
            }
          />

          {mode === 'products' && tagFilter.length > 0 && (
            <Banner tone="info">
              <BlockStack gap="100">
                <Text as="p" variant="bodySm">
                  {t("Showing products that have these tags:")}
                </Text>
                <InlineStack gap="100" wrap>
                  {tagFilter.map((t) => (
                    <Badge key={t}>{t}</Badge>
                  ))}
                </InlineStack>
              </BlockStack>
            </Banner>
          )}

          {sourceNote && (
            <Banner tone="info">
              <p>{sourceNote}</p>
            </Banner>
          )}
          {error && (
            <Banner tone="critical">
              <p>{error}</p>
            </Banner>
          )}

          {loading ? (
            <Box padding="400">
              <InlineStack align="center" gap="200">
                <Spinner size="small" />
                <Text as="span" tone="subdued">
                  {t("Loading from Shopify…")}
                </Text>
              </InlineStack>
            </Box>
          ) : items.length === 0 ? (
            <Text as="p" tone="subdued">
              {t("No")} {mode} found
              {debouncedQ ? ` matching “${debouncedQ}”` : ''}
              {mode === 'products' && tagFilter.length
                ? ` with tag${tagFilter.length === 1 ? '' : 's'} “${tagFilter.join(', ')}”`
                : ''}
              .
            </Text>
          ) : mode === 'collections' ? (
            <BlockStack gap="200">
              {items.map((item) => {
                const selected = isSelected(item.id);
                return (
                  <div
                    key={item.id}
                    role="button"
                    tabIndex={0}
                    onClick={() => onToggle(item)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onToggle(item);
                      }
                    }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 12,
                      padding: '10px 8px',
                      borderRadius: 8,
                      cursor: 'pointer',
                      background: selected ? '#F1F8F5' : 'transparent',
                      outline: selected ? '1px solid #AEE9D1' : 'none',
                    }}
                  >
                    <InlineStack gap="300" blockAlign="center" wrap={false}>
                      <span
                        aria-hidden
                        style={{
                          flexShrink: 0,
                          width: 20,
                          height: 20,
                          borderRadius: 4,
                          border: selected
                            ? '2px solid #008060'
                            : '2px solid #8C9196',
                          background: selected ? '#008060' : '#fff',
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          boxSizing: 'border-box',
                        }}
                      >
                        {selected ? (
                          <svg
                            width="12"
                            height="12"
                            viewBox="0 0 12 12"
                            fill="none"
                            aria-hidden
                          >
                            <path
                              d="M2.5 6.2L4.8 8.5L9.5 3.5"
                              stroke="#fff"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        ) : null}
                      </span>
                      <Thumbnail
                        source={
                          item.imageUrl ||
                          'https://cdn.shopify.com/s/files/1/0533/2089/files/placeholder-images-image_large.png'
                        }
                        alt={item.title}
                        size="small"
                      />
                      <BlockStack gap="050">
                        <Text as="span" fontWeight="semibold">
                          {item.title}
                        </Text>
                        <Text as="span" tone="subdued" variant="bodySm">
                          {[
                            item.handle,
                            item.productCount != null
                              ? `${item.productCount} products`
                              : null,
                          ]
                            .filter(Boolean)
                            .join(' · ')}
                        </Text>
                      </BlockStack>
                    </InlineStack>
                    {selected ? (
                      <Badge tone="success">{t("Selected")}</Badge>
                    ) : (
                      <Text as="span" tone="subdued" variant="bodySm">
                        {t("Click to select")}
                      </Text>
                    )}
                  </div>
                );
              })}
              {hasNextPage && (
                <Button
                  fullWidth
                  loading={loadingMore}
                  onClick={() => void load({ after: endCursor, append: true })}
                >
                  {t("Load more")}
                </Button>
              )}
            </BlockStack>
          ) : (
            <BlockStack gap="0">
              {items.map((product) => {
                const vs = product.variants || [];
                const multi = vs.length > 1;
                const isOpen = !!expanded[product.id];
                const full = productFullySelected(product);
                const partial = productPartiallySelected(product);
                const nSel = selectedVariantCount(product);
                const oos = productOutOfStock(product);
                const rowSelected = full || partial;

                return (
                  <div
                    key={product.id}
                    style={{
                      borderBottom: '1px solid #E3E5E8',
                      padding: '10px 8px',
                      margin: '0 -8px',
                      borderRadius: 8,
                      background: rowSelected ? '#F1F8F5' : 'transparent',
                      outline: rowSelected ? '1px solid #AEE9D1' : 'none',
                    }}
                  >
                    <InlineStack
                      align="space-between"
                      blockAlign="center"
                      wrap={false}
                      gap="200"
                    >
                      <div
                        role="checkbox"
                        aria-checked={full ? true : partial ? 'mixed' : false}
                        aria-label={product.title}
                        tabIndex={0}
                        onClick={() => toggleProductAll(product)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            toggleProductAll(product);
                          }
                        }}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 12,
                          flex: 1,
                          minWidth: 0,
                          cursor: 'pointer',
                        }}
                      >
                        {/* Explicit selection control — empty Polaris label was invisible */}
                        <span
                          aria-hidden
                          style={{
                            flexShrink: 0,
                            width: 20,
                            height: 20,
                            borderRadius: 4,
                            border: full
                              ? '2px solid #008060'
                              : partial
                                ? '2px solid #008060'
                                : '2px solid #8C9196',
                            background: full
                              ? '#008060'
                              : partial
                                ? '#008060'
                                : '#fff',
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            boxSizing: 'border-box',
                          }}
                        >
                          {full ? (
                            <svg
                              width="12"
                              height="12"
                              viewBox="0 0 12 12"
                              fill="none"
                              aria-hidden
                            >
                              <path
                                d="M2.5 6.2L4.8 8.5L9.5 3.5"
                                stroke="#fff"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          ) : partial ? (
                            <span
                              style={{
                                width: 10,
                                height: 2,
                                background: '#fff',
                                borderRadius: 1,
                              }}
                            />
                          ) : null}
                        </span>
                        <Thumbnail
                          source={
                            product.imageUrl ||
                            'https://cdn.shopify.com/s/files/1/0533/2089/files/placeholder-images-image_large.png'
                          }
                          alt={product.title}
                          size="small"
                        />
                        <BlockStack gap="050">
                          <InlineStack gap="150" blockAlign="center">
                            <Text as="span" fontWeight="semibold">
                              {product.title}
                            </Text>
                            {oos && (
                              <Badge tone="warning">{t("Out of Stock")}</Badge>
                            )}
                            {partial && !oos && (
                              <Badge tone="info">{`${nSel} variant${nSel === 1 ? '' : 's'}`}</Badge>
                            )}
                            {full && multi && !oos && (
                              <Badge tone="success">{t("All variants")}</Badge>
                            )}
                            {full && !multi && !oos && (
                              <Badge tone="success">{t("Selected")}</Badge>
                            )}
                          </InlineStack>
                          <Text as="span" tone="subdued" variant="bodySm">
                            {multi
                              ? `${vs.length} variants`
                              : product.price
                                ? String(product.price)
                                : product.handle || '1 variant'}
                            {product.price && multi
                              ? ` · from ${product.price}`
                              : product.price && !multi
                                ? ` · ${product.price}`
                                : ''}
                          </Text>
                        </BlockStack>
                      </div>
                      {multi ? (
                        <Button
                          size="slim"
                          variant="plain"
                          onClick={() =>
                            setExpanded((e) => ({
                              ...e,
                              [product.id]: !isOpen,
                            }))
                          }
                        >
                          {isOpen ? 'Hide options' : 'Show options'}
                        </Button>
                      ) : (
                        <Text as="span" tone="subdued" variant="bodySm">
                          {product.price || ''}
                        </Text>
                      )}
                    </InlineStack>

                    {multi && (
                      <Collapsible open={isOpen} id={`vars-${product.id}`}>
                        <Box paddingBlockStart="200" paddingInlineStart="400">
                          <BlockStack gap="100">
                            {vs.map((v) => {
                              const vSelected = variantRowSelected(product, v.id);
                              const soldOut = v.available === false;
                              return (
                                <div
                                  key={v.id}
                                  role="button"
                                  tabIndex={0}
                                  onClick={() => toggleVariant(product, v)}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                      e.preventDefault();
                                      toggleVariant(product, v);
                                    }
                                  }}
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    gap: 12,
                                    padding: '8px 10px',
                                    borderRadius: 8,
                                    cursor: 'pointer',
                                    background: vSelected
                                      ? '#E3F1DF'
                                      : '#F6F6F7',
                                    border: vSelected
                                      ? '1px solid #AEE9D1'
                                      : '1px solid transparent',
                                  }}
                                >
                                  <InlineStack gap="200" blockAlign="center">
                                    <span
                                      aria-hidden
                                      style={{
                                        flexShrink: 0,
                                        width: 18,
                                        height: 18,
                                        borderRadius: 4,
                                        border: vSelected
                                          ? '2px solid #008060'
                                          : '2px solid #8C9196',
                                        background: vSelected
                                          ? '#008060'
                                          : '#fff',
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        boxSizing: 'border-box',
                                      }}
                                    >
                                      {vSelected ? (
                                        <svg
                                          width="11"
                                          height="11"
                                          viewBox="0 0 12 12"
                                          fill="none"
                                          aria-hidden
                                        >
                                          <path
                                            d="M2.5 6.2L4.8 8.5L9.5 3.5"
                                            stroke="#fff"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                          />
                                        </svg>
                                      ) : null}
                                    </span>
                                    <Text as="span" variant="bodySm">
                                      {variantLabel(v)}
                                    </Text>
                                  </InlineStack>
                                  <Text as="span" tone="subdued" variant="bodySm">
                                    {soldOut
                                      ? 'Out of Stock'
                                      : v.price
                                        ? v.price
                                        : ''}
                                  </Text>
                                </div>
                              );
                            })}
                          </BlockStack>
                        </Box>
                      </Collapsible>
                    )}
                  </div>
                );
              })}
              {hasNextPage && (
                <Box paddingBlockStart="300">
                  <Button
                    fullWidth
                    loading={loadingMore}
                    onClick={() => void load({ after: endCursor, append: true })}
                  >
                    {t("Load more")}
                  </Button>
                </Box>
              )}
            </BlockStack>
          )}

          <Text as="p" tone="subdued" variant="bodySm">
            {selectedCount} {mode === 'products' ? 'item' : 'collection'}
            {selectedCount === 1 ? '' : 's'} selected
            {mode === 'products' ? ' (variants count separately)' : ''}
          </Text>
        </BlockStack>
      </Modal.Section>
    </Modal>
  );
}
